import {managedWorkspace,productFetch} from "../platform/managed";
import type { ApiErrorPayload } from "./types";

const LOCAL_DEVELOPMENT_PORTS = new Set(["3000", "3001", "5173"]);
const TOKEN_STORAGE_KEY = "crm_token";
const API_BASE_STORAGE_KEY = "crm_api_base";
export const AUTH_SESSION_ENDED_EVENT = "crm:auth-session-ended";
export const AUTH_SESSION_CHANGED_EVENT = "crm:auth-session-changed";

const SESSION_ENDED_ERRORS = new Set([
  "Invalid or expired token",
  "Your session ended after a security change. Sign in again.",
]);

function defaultApiBase(): string {
  const configured = import.meta.env.VITE_API_BASE_URL?.trim();
  if (configured) return configured.replace(/\/$/, "");
  if (location.protocol === "file:" || LOCAL_DEVELOPMENT_PORTS.has(location.port)) {
    return "http://127.0.0.1:8000";
  }
  return location.origin;
}

function configuredApiBase(): string {
  const saved = localStorage.getItem(API_BASE_STORAGE_KEY);
  const isHosted = !["localhost", "127.0.0.1", ""].includes(location.hostname);
  if (isHosted && saved && /^http:\/\/(127\.0\.0\.1|localhost):/i.test(saved)) {
    localStorage.removeItem(API_BASE_STORAGE_KEY);
    return defaultApiBase();
  }
  return (saved || defaultApiBase()).replace(/\/$/, "");
}

function requestHeaders(init: RequestInit, token: string | null): Headers {
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return headers;
}

async function errorPayload(response: Response): Promise<ApiErrorPayload> {
  const text = await response.text();
  if (!text) return { error: `Request failed: ${response.status}` };
  try {
    return JSON.parse(text) as ApiErrorPayload;
  } catch {
    return { error: text || `Request failed: ${response.status}` };
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly payload: ApiErrorPayload;

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.error || `Request failed: ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  window.dispatchEvent(new Event(AUTH_SESSION_CHANGED_EVENT));
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  window.dispatchEvent(new Event(AUTH_SESSION_CHANGED_EVENT));
}

export function subscribeSessionToken(onChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === TOKEN_STORAGE_KEY || event.key === null) onChange();
  };
  window.addEventListener(AUTH_SESSION_CHANGED_EVENT, onChange);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, onChange);
    window.removeEventListener("storage", onStorage);
  };
}

function announceEndedSession(status: number, payload: ApiErrorPayload, requestToken: string | null): void {
  if (
    status !== 401
    || !requestToken
    || !SESSION_ENDED_ERRORS.has(payload.error || "")
    || getStoredToken() !== requestToken
  ) return;

  window.dispatchEvent(new Event(AUTH_SESSION_ENDED_EVENT));
}

export interface ApiRequestInit extends RequestInit {
  timeoutMs?: number;
}

export async function apiRequest<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const { timeoutMs, signal, ...requestInit } = init;
  const controller = timeoutMs ? new AbortController() : null;
  let timedOut = false;
  const abort = () => controller?.abort(signal?.reason);
  if (signal?.aborted) abort();
  else signal?.addEventListener("abort", abort, { once: true });
  const timer = timeoutMs ? setTimeout(() => {
    timedOut = true;
    controller?.abort();
  }, timeoutMs) : undefined;
  try {
    const requestToken = getStoredToken();
    const response = await productFetch(managedWorkspace() ? `${location.origin}${path}` : `${configuredApiBase()}${path}`, { ...requestInit, signal: controller?.signal || signal, headers: requestHeaders(requestInit, requestToken) });
    const text = await response.text();
    let payload: unknown = {};
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = { error: `Unexpected response from the server (${response.status})` };
      }
    }
    if (!response.ok) {
      const error = payload as ApiErrorPayload;
      announceEndedSession(response.status, error, requestToken);
      throw new ApiError(response.status, error);
    }
    return payload as T;
  } catch (error) {
    if (timedOut) throw new Error("The request is taking too long to respond. Please try again.");
    throw error;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

export async function apiDownload(path: string, init: RequestInit = {}): Promise<Blob> {
  return (await apiDownloadResponse(path, init)).blob;
}

export interface ApiDownloadResponse {
  blob: Blob;
  headers: Headers;
}

export async function apiDownloadResponse(path: string, init: RequestInit = {}): Promise<ApiDownloadResponse> {
  const requestToken = getStoredToken();
  const response = await productFetch(managedWorkspace() ? `${location.origin}${path}` : `${configuredApiBase()}${path}`, { ...init, headers: requestHeaders(init, requestToken) });
  if (!response.ok) {
    const error = await errorPayload(response);
    announceEndedSession(response.status, error, requestToken);
    throw new ApiError(response.status, error);
  }
  return { blob: await response.blob(), headers: response.headers };
}
